import { db } from "../firebaseClient"
import { doc, getDoc, setDoc, collection, getDocs, query, where, serverTimestamp } from "firebase/firestore"
import { createEnrollment, getLearnerIdentity } from "./enrollmentHelpers"
import { getCourse } from "./courseHelpers"

// Mirrors enrollmentDocId's userId_courseId pattern — one record per
// learner per path, deterministic ID so double-enrollment is structurally
// impossible and "am I already on this path" is a single getDoc.
export const pathEnrollmentDocId = (userId, pathId) => `${userId}_${pathId}`

const countLessons = (course) => {
  let total = 0
  course?.modules?.forEach((m) => { total += m?.lessons?.length || 0 })
  return total
}

// Joining a path immediately creates real course enrollments for every
// node — auto-enroll, no lighter "track only" mode. Uses the existing
// createEnrollment(), which is itself idempotent, so a node course the
// learner is already enrolled in is left untouched (no progress reset).
// Sequential (not Promise.all) — simpler to reason about, fine at the
// path lengths this product expects.
export const createPathEnrollment = async ({ userId, pathId, orgId, listing }) => {
  if (!userId || !pathId || !orgId || !listing) {
    return { pathEnrollment: null, error: { message: "Missing required fields" } }
  }

  try {
    const pathEnrollmentRef = doc(db, "pathEnrollments", pathEnrollmentDocId(userId, pathId))
    const existing = await getDoc(pathEnrollmentRef)
    if (existing.exists()) {
      return { pathEnrollment: existing.data(), error: null }
    }

    const identity = await getLearnerIdentity(userId)
    const nodeCourseIds = (listing?.nodes || []).map((n) => n?.courseId).filter(Boolean)

    for (const courseId of nodeCourseIds) {
      const { course } = await getCourse(orgId, courseId)
      if (!course) continue
      await createEnrollment({
        userId,
        courseId,
        orgId,
        enrollmentType: "path",
        lessonsTotal: countLessons(course),
        learnerName: identity.name,
        learnerEmail: identity.email,
        learnerAvatarUrl: identity.avatarUrl,
        learnerNesolearnId: identity.nesolearnId,
      })
    }

    const pathEnrollmentData = {
      userId,
      pathId,
      orgId,
      title: listing?.title || "",
      nodeCourseIds,
      enrolledAt: serverTimestamp(),
    }
    await setDoc(pathEnrollmentRef, pathEnrollmentData)

    return { pathEnrollment: pathEnrollmentData, error: null }
  } catch (error) {
    return { pathEnrollment: null, error }
  }
}

// Every path this learner has joined, across all orgs — flat collection,
// mirrors listUserEnrollments.
export const listUserPathEnrollments = async (userId) => {
  if (!userId) return { pathEnrollments: [], error: null }
  try {
    const ref = collection(db, "pathEnrollments")
    const q = query(ref, where("userId", "==", userId))
    const snap = await getDocs(q)
    return { pathEnrollments: snap.docs?.map((d) => d.data()), error: null }
  } catch (error) {
    return { pathEnrollments: [], error }
  }
}

// Percent complete across a path's node courses, given the learner's own
// course-enrollment map (courseId -> enrollment). No extra reads — the
// dashboard already has this from its own enrolledCourses fetch.
export const computePathProgress = (nodeCourseIds, enrollmentByCourseId) => {
  const total = nodeCourseIds?.length || 0
  if (total === 0) return { completed: 0, total: 0, percent: 0 }
  const completed = nodeCourseIds.filter(
    (courseId) => (enrollmentByCourseId?.[courseId]?.progressPercent || 0) >= 100
  ).length
  return { completed, total, percent: Math.round((completed / total) * 100) }
}